import { createAsyncThunk } from '@reduxjs/toolkit';
import { entries, flatten, values } from 'lodash';
import {
  ISharedDatasourcesResponse,
  getSharedDatasources,
  setSharedSources,
} from '@qodly/shared';
import { AppState } from '~store/modules';
import { setContent } from '~store/modules/root';
import { SHARED_USER_NAMESPACE, sameDefaultUserSource } from './utils';

export const fetchSharedDatasources = createAsyncThunk<
  ISharedDatasourcesResponse,
  void,
  {
    rejectValue: string;
  }
>('shared_datasources/fetchSharedSources', async (_, { rejectWithValue }) => {
  try {
    const datasources = await getSharedDatasources();

    let sourcesWithUserSources = {
      ...datasources,
    };

    const _userSources: ISharedDatasourcesResponse = {};
    let changeFlag = false;

    entries(SHARED_USER_NAMESPACE).forEach(([shared_ns, sources]) => {
      const newSources: datasources.ICreateDataSource[] = [];
      //server side.
      const lastSources = sourcesWithUserSources[shared_ns];

      if (lastSources && lastSources.length) {
        sources.forEach(sourceToAdd => {
          const found = lastSources.find(lastSource => lastSource.id === sourceToAdd.id);
          if (!found) {
            changeFlag = true
            newSources.push(sourceToAdd)
          }
          else {
            if (sameDefaultUserSource(found, sourceToAdd)) {
              newSources.push(found)
            }
          }
        })
      } else {
        newSources.push(...sources);
        changeFlag = true;
      }

      _userSources[shared_ns] = newSources;
    });
    //update
    sourcesWithUserSources = {
      ...sourcesWithUserSources,
      ..._userSources,
    };

    // save the new ones.
    if (changeFlag) {
      await setSharedSources(sourcesWithUserSources);
    }

    return sourcesWithUserSources;
  } catch (e) {
    return rejectWithValue('Error while loading the shared datasources');
  }
});

export const addSharedSource = createAsyncThunk<
  ISharedDatasourcesResponse,
  datasources.ICreateDataSource,
  {
    rejectValue: string;
    state: AppState;
  }
>(
  'shared_datasources/addSharedSource',
  async (content, { rejectWithValue, getState }) => {
    try {
      const state = JSON.parse(JSON.stringify(getState().sharedDatasources));
      if (!content.namespace) {
        return rejectWithValue('Namespace is required');
      } else {
        if (!state.sources[content.namespace]) {
          state.sources[content.namespace] = [content];
        } else {
          state.sources[content.namespace].push(content);
        }
      }
      await setSharedSources(state.sources);
      return state.sources;
    } catch (e) {
      return rejectWithValue('Error while saving the shared datasources');
    }
  },
);

export const editSharedSource = createAsyncThunk(
  'shared_datasources/editSharedSource',
  async (
    payload: datasources.ICreateDataSource,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = (getState() as AppState).sharedDatasources;
      if (!payload.namespace) {
        return rejectWithValue('Namespace is required');
      }

      if (state.sources[payload.namespace]) {
        state.sources[payload.namespace] = state.sources[payload.namespace].map(
          (ds) => {
            if (ds.id === payload.id) {
              return {
                ...payload,
                id: ds.id,
              };
            }
            return ds;
          },
        );
      } else {
        return rejectWithValue('Namespace not found');
      }
      await setSharedSources(state.sources);
      return state.sources;
    } catch (e) {
      return rejectWithValue('Error while saving the shared datasources');
    }
  },
);

export const deleteSharedSource = createAsyncThunk<
  ISharedDatasourcesResponse,
  { id: string; namespace: string },
  {
    rejectValue: string;
    state: AppState;
  }
>(
  'shared_datasources/deleteSharedSource',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = { ...getState().sharedDatasources };
      if (state.sources[payload.namespace]) {
        const namespace = state.sources[payload.namespace].filter(
          (ds) => ds.id !== payload.id,
        );
        if (namespace.length === 0) {
          const { [payload.namespace]: _, ...rest } = state.sources;
          state.sources = rest;
        } else {
          state.sources = {
            ...state.sources,
            [payload.namespace]: namespace,
          };
        }
      }
      await setSharedSources(state.sources);
      return state.sources;
    } catch (e) {
      return rejectWithValue('Error while saving the shared datasources');
    }
  },
);

export const updateSharedDatasources = createAsyncThunk<
  ISharedDatasourcesResponse,
  datasources.ICreateDataSource[],
  {
    rejectValue: string;
    state: AppState;
  }
>(
  'shared_datasources/updateSharedDatasources',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const state = { ...getState().sharedDatasources };

      for (const source of payload) {
        if (!source.namespace) {
          return rejectWithValue('Namespace is required');
        }

        if (state.sources[source.namespace]) {
          state.sources[source.namespace] = state.sources[source.namespace].map(
            (ds) => {
              if (ds.id === source.id) {
                return source;
              }
              return ds;
            },
          );
        } else {
          return rejectWithValue('Namespace not found');
        }
      }

      await setSharedSources(state.sources);
      return state.sources;
    } catch (e) {
      return rejectWithValue('Error while saving the shared datasources');
    }
  },
);

export const setDatasourceEvents = createAsyncThunk<
  void,
  {
    tabPath: string;
    name: string;
    namespace: string;
    events: webforms.WEvent[];
  },
  {
    state: AppState;
  }
>(
  'shared_datasources/setDatasourceEvents',
  async ({ tabPath, name, namespace, events }, { getState, dispatch }) => {
    const {
      root: { tabs },
      sharedDatasources,
    } = getState();
    const localDatasources: datasources.ICreateDataSource[] =
      tabs.find(({ path }) => path === tabPath)?.content?.metadata
        ?.datasources || [];
    const hasChanged = {
      local: false,
      shared: false,
    };

    const updateEvents = (
      def: datasources.ICreateDataSource,
      onChange?: () => void,
    ) => {
      if (
        def.id === name &&
        ((!namespace && !def.namespace) || def.namespace === namespace)
      ) {
        onChange?.();
        return {
          ...def,
          events,
        };
      }
      return def;
    };

    const newLocalDefinitions = localDatasources.map((def) =>
      updateEvents(def, () => {
        hasChanged.local = true;
      }),
    );

    const newSharedDefinitions = flatten(values(sharedDatasources.sources)).map(
      (def) =>
        updateEvents(def, () => {
          hasChanged.shared = true;
        }),
    );

    if (hasChanged.local) {
      dispatch(
        setContent({
          path: tabPath,
          content: newLocalDefinitions,
          contentPath: 'metadata.datasources',
        }),
      );
    }

    if (hasChanged.shared) {
      dispatch(updateSharedDatasources(newSharedDefinitions));
    }
  },
);
